<template>
  <div
    class="tw:relative tw:flex tw:w-full tw:max-w-[480px] tw:flex-col tw:items-start tw:gap-4 tw:rounded-sm tw:border tw:border-solid tw:border-denim-200 tw:bg-white tw:p-4 tw:text-denim-900 tw:shadow-lg"
    data-testid="upsell-card"
  >
    <BaseButton
      v-if="showCloseButton"
      circle
      class="tw:absolute tw:right-1 tw:top-1"
      color="grey"
      data-testid="upsell-card-close-button"
      flat
      icon="close"
      new-design
      new-icon
      @click="emit('close')"
    />

    <div
      v-if="types.length > 1"
      class="tw:flex tw:w-full tw:gap-4 tw:border-b tw:border-denim-200 tw:pb-2"
      data-testid="upsell-card-tabs"
      role="tablist"
    >
      <button
        v-for="feat in types"
        :key="feat"
        :aria-selected="activeTab === feat"
        class="tw:cursor-pointer tw:border-none tw:bg-transparent tw:px-0 tw:pb-1 tw:text-sm tw:font-medium tw:transition-colors"
        :class="
          twMerge(
            activeTab === feat ? TAB_ACTIVE_CLASSES[feat] : TAB_INACTIVE_CLASSES
          )
        "
        :data-testid="`upsell-card-tab-${feat}`"
        role="tab"
        type="button"
        @click="activeTab = feat"
      >
        {{ componentText(`${feat}.tab_label`) }}
      </button>
    </div>

    <div
      class="tw:w-full tw:text-center tw:text-lg tw:font-bold"
      data-testid="upsell-card-title"
    >
      {{ title }}
    </div>
    <div
      class="tw:text-left tw:text-sm"
      data-testid="upsell-card-description"
    >
      <slot>
        {{ description }}
      </slot>
    </div>
    <BaseButton
      class="tw:w-full tw:border-0!"
      :class="submitButtonColor"
      color="insights"
      data-testid="upsell-card-button"
      :href="submitButtonHref"
      :label="submitButtonLabel"
      new-design
      rel="noopener noreferrer"
      target="_blank"
      type="a"
      @click="handleClick"
    />
  </div>
</template>

<script setup lang="ts">
import { twMerge } from "tailwind-merge";
import { computed, watch } from "vue";

import { track } from "shared/boot/analytics";
import { getLocaleText } from "shared/boot/i18n";
import { BaseButton } from "shared/components/base";
import useSupportLinking from "shared/composables/useSupportLinking";
import { getAppConfig } from "shared/helpers/useAppConfig";
import { AppSection, type Nullable } from "shared/types";

const TAB_INACTIVE_CLASSES = "tw:text-denim-500 tw:hover:text-denim-700";

const TAB_ACTIVE_CLASSES: Record<AppSection, string> = {
  [AppSection.MONITORING]: "tw:border-b-2 tw:border-blue-700 tw:text-blue-700",
  [AppSection.OUTREACH]: "tw:border-b-2 tw:border-blue-700 tw:text-blue-700",
  [AppSection.SOCIAL]: "tw:border-b-2 tw:border-purple-600 tw:text-purple-600",
  [AppSection.AI_VISIBILITY]:
    "tw:border-b-2 tw:border-teal-700 tw:text-teal-700",
};

export interface UpsellCardProps {
  buttonLabel?: Nullable<string>;
  moreInfoUrl?: Nullable<string>;
  origin?: Nullable<string>;
  showCloseButton?: boolean;
  types: AppSection[];
}

const props = withDefaults(defineProps<UpsellCardProps>(), {
  buttonLabel: null,
  moreInfoUrl: null,
  origin: null,
});

const emit = defineEmits<{
  close: [];
}>();

const activeTab = defineModel<AppSection>("activeTab", {
  default: AppSection.MONITORING,
});

const appConfig = getAppConfig();

const { mailTo } = useSupportLinking();

watch(
  () => props.types,
  (newTypes) => {
    if (newTypes.length > 0 && !newTypes.includes(activeTab.value)) {
      const [firstType] = newTypes;
      activeTab.value = firstType;
    }
  },
  { immediate: true }
);

function componentText(key: string, options: Record<string, string> = {}) {
  return getLocaleText(`upsell_card.${key}`, options);
}

const title = computed<string>(() => componentText(`${activeTab.value}.title`));

const description = computed<string>(() =>
  componentText(`${activeTab.value}.description`, {
    platformName: appConfig.value.platformName,
  })
);

const submitButtonLabel = computed<string>(() => {
  if (props.buttonLabel) return props.buttonLabel;

  return props.moreInfoUrl
    ? componentText("more_info")
    : componentText("get_in_touch");
});

const submitButtonColor = computed<string>(() => {
  if (activeTab.value === AppSection.SOCIAL) {
    return "tw:bg-purple-600! tw:hover:bg-purple-500!";
  }

  return "tw:bg-blue-700! tw:hover:bg-blue-600!";
});

const submitButtonHref = computed<string>(
  () => props.moreInfoUrl ?? mailTo.value
);

function handleClick() {
  track("Clicked Upsell card button", {
    origin: props.origin,
    type: activeTab.value,
    url: submitButtonHref.value,
  });
}
</script>
